import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  ShoppingCart,
  CreditCard,
  FileText,
  Settings,
  X,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Wallet,
  BarChart3,
  Receipt,
  Package,
  Truck,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Tag,
  Calendar,
  Trash2,
  Shield,
  Calculator,
  Briefcase,
  FileSpreadsheet,
  Tags,
  BookOpen,
  PieChart,
  CheckSquare,
  Trophy,
  Gift,
  Lock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useModule } from '../../contexts/ModuleContext';
import { useBranding } from '../../contexts/BrandingContext';

const WholesaleSidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { activeModule } = useModule();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState([]);

  // List of subgroup IDs for special handling
  const settingsSubgroupIds = [
    'general-settings',
    'finance-settings',
    'product-settings',
    'communication-settings',
    'system-settings'
  ];

  // Accordion behavior for top-level groups, subgroups accordion within Settings
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      // If clicking a subgroup within settings
      if (settingsSubgroupIds.includes(sectionId)) {
        // Keep 'settings' open, toggle subgroup (only one subgroup open at a time)
        const otherSubgroups = prev.filter((id) => !settingsSubgroupIds.includes(id));
        if (prev.includes(sectionId)) {
          return otherSubgroups; // Close subgroup, keep settings open
        }
        return [...otherSubgroups, sectionId]; // Open this subgroup, close other subgroups
      }

      // Top-level group: close all and toggle
      return prev.includes(sectionId) ? [] : [sectionId];
    });
  };

  const { config } = useBranding();

  // Dynamically derive wholesale-specific or fallback branding settings from context
  const branding = useMemo(() => {
    if (config.use_separate_wholesale_branding) {
      return {
        company_name: config.wholesale_company_name || config.company_name || 'Wholesale',
        logo_url: config.wholesale_logo_url || config.logo_url,
        logo_type: config.wholesale_logo_type || config.logo_type || 'icon',
        logo_icon_text: config.wholesale_logo_icon_text || config.logo_icon_text || 'WS',
        sidebar_name_size:
          config.wholesale_sidebar_name_size || config.sidebar_name_size || 'medium'
      };
    }
    return {
      company_name: config.company_name || 'Wholesale',
      logo_url: config.logo_url,
      logo_type: config.logo_type || 'icon',
      logo_icon_text: config.logo_icon_text || 'WS',
      sidebar_name_size: config.sidebar_name_size || 'medium'
    };
  }, [config]);

  const navigationGroups = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: LayoutDashboard,
      href: '/wholesale/dashboard',
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      standalone: true
    },
    {
      id: 'orders',
      name: 'Orders',
      icon: ShoppingCart,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      children: [
        { name: 'All Orders', href: '/wholesale/orders', icon: ShoppingCart },
        { name: 'Pending', href: '/wholesale/orders?status=pending', icon: Clock },
        { name: 'In Progress', href: '/wholesale/orders?status=in_progress', icon: Truck },
        { name: 'Completed', href: '/wholesale/orders?status=completed', icon: CheckCircle },
        { name: 'WhatsApp Orders', href: '/wholesale/whatsapp-orders', icon: MessageSquare }
      ]
    },
    {
      id: 'distributors',
      name: 'Customers',
      icon: Users,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      children: [
        { name: 'All Customers', href: '/wholesale/distributors', icon: Users },
        { name: 'Performance Board', href: '/wholesale/distributors/leaderboard', icon: Trophy },
        { name: 'Pricing Tiers', href: '/wholesale/pricing-tiers', icon: Tag },
        { name: 'Credit Management', href: '/wholesale/credit', icon: CreditCard }
      ]
    },
    {
      id: 'companies',
      name: 'Manufacturing',
      icon: Building2,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      children: [
        { name: 'Companies', href: '/wholesale/companies', icon: Building2 },
        { name: 'Products', href: '/wholesale/products', icon: Package },
        { name: 'Rebates', href: '/wholesale/rebates', icon: Gift }
      ]
    },
    {
      id: 'payments',
      name: 'Payments',
      icon: Wallet,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      children: [
        { name: 'All Payments', href: '/wholesale/payments', icon: Wallet },
        { name: 'Pending', href: '/wholesale/payments?status=pending', icon: Clock },
        { name: 'Overdue', href: '/wholesale/payments?status=overdue', icon: AlertCircle }
      ]
    },
    {
      id: 'documents',
      name: 'Documents',
      icon: FileText,
      href: '/wholesale/invoices',
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      standalone: true
    },
    {
      id: 'expenses',
      name: 'Expenses',
      icon: Wallet,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      children: [
        { name: 'My Expenses', href: '/wholesale/expenses', icon: Wallet },
        { name: 'Budgets', href: '/wholesale/budgets', icon: PieChart },
        { name: 'Analytics', href: '/wholesale/expenses/analytics', icon: BarChart3 },
        { name: 'Categories', href: '/wholesale/expense-categories', icon: Tag }
      ]
    },
    {
      id: 'payroll',
      name: 'Payroll',
      icon: Wallet,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      children: [
        { name: 'Dashboard', href: '/wholesale/payroll', icon: LayoutDashboard },
        { name: 'Salaries', href: '/wholesale/payroll/salaries', icon: Users },
        { name: 'Payroll Runs', href: '/wholesale/payroll/runs', icon: Calendar },
        { name: 'Loans', href: '/wholesale/payroll/loans', icon: CreditCard },
        { name: 'Payslips', href: '/wholesale/payroll/payslips', icon: FileText }
      ]
    },
    {
      id: 'accounting',
      name: 'Accounting',
      icon: Calculator,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      children: [
        { name: 'Chart of Accounts', href: '/wholesale/accounting/accounts', icon: BookOpen },
        { name: 'Cost Centers', href: '/wholesale/accounting/cost-centers', icon: PieChart },
        { name: 'Fixed Assets', href: '/wholesale/accounting/fixed-assets', icon: Building2 },
        { name: 'Bank Recon', href: '/wholesale/accounting/reconciliation', icon: CheckSquare },
        {
          name: 'Journal Entries',
          href: '/wholesale/accounting/journal-entries',
          icon: Calculator
        },
        { name: 'Vendors', href: '/wholesale/accounting/vendors', icon: Briefcase },
        { name: 'Bills (AP)', href: '/wholesale/accounting/bills', icon: CreditCard },
        { name: 'Invoices (AR)', href: '/wholesale/accounting/invoices', icon: FileSpreadsheet }
      ]
    },
    {
      id: 'reports',
      name: 'Financial Reports',
      icon: BarChart3,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      children: [
        {
          name: 'Statement of Account',
          href: '/wholesale/reports/statement-of-account',
          icon: FileText
        },
        { name: 'Reports Hub', href: '/wholesale/reports', icon: BarChart3 },
        { name: 'Sales Report', href: '/wholesale/reports/sales', icon: TrendingUp },
        { name: 'Customer Performance', href: '/wholesale/reports/distributors', icon: Users },
        { name: 'Revenue Analysis', href: '/wholesale/reports/revenue', icon: DollarSign },
        { name: 'Income Statement', href: '/wholesale/reports/income-statement', icon: FileText },
        { name: 'Cash Flow', href: '/wholesale/reports/cash-flow', icon: Wallet },
        { name: 'Balance Sheet', href: '/wholesale/reports/balance-sheet', icon: FileSpreadsheet },
        { name: 'Trial Balance', href: '/wholesale/reports/trial-balance', icon: BookOpen },
        { name: 'VAT Tracking', href: '/wholesale/reports/vat-tracking', icon: Receipt }
      ]
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Settings,
      roles: ['super_admin', 'system_admin', 'wholesale_admin'],
      subgroups: [
        {
          id: 'general-settings',
          name: 'General',
          icon: Building2,
          children: [
            { name: 'Branding', href: '/wholesale/settings/branding', icon: Building2 },
            { name: 'Users', href: '/wholesale/settings/users', icon: Users },
            {
              name: 'Account Settings',
              href: '/wholesale/settings/account',
              icon: Shield,
              roles: ['super_admin', 'system_admin']
            },
            {
              name: 'Security',
              href: '/wholesale/settings/security',
              icon: Lock,
              roles: ['super_admin', 'system_admin']
            }
          ]
        },
        {
          id: 'finance-settings',
          name: 'Finance',
          icon: DollarSign,
          children: [
            { name: 'Bank Channels', href: '/wholesale/settings/bank-channels', icon: CreditCard },
            { name: 'Tax Settings', href: '/wholesale/settings/tax', icon: DollarSign },
            { name: 'Fiscal Periods', href: '/wholesale/settings/fiscal-periods', icon: Calendar },
            { name: 'Pricing Rules', href: '/wholesale/settings/pricing', icon: Tag }
          ]
        },
        {
          id: 'product-settings',
          name: 'Catalog',
          icon: Package,
          children: [
            { name: 'Product Categories', href: '/wholesale/settings/categories', icon: Package },
            { name: 'Document Settings', href: '/wholesale/settings/documents', icon: Receipt }
          ]
        },
        {
          id: 'communication-settings',
          name: 'Communications',
          icon: MessageSquare,
          children: [
            { name: 'WhatsApp Config', href: '/wholesale/settings/whatsapp', icon: MessageSquare },
            { name: 'Email Config', href: '/wholesale/settings/email', icon: FileText }
          ]
        },
        {
          id: 'system-settings',
          name: 'System',
          icon: Shield,
          children: [
            { name: 'Audit Logs', href: '/wholesale/settings/audit-logs', icon: FileText },
            { name: 'Recycle Bin', href: '/wholesale/recycle-bin', icon: Trash2 },
            { name: 'Purge Management', href: '/wholesale/purge-management', icon: Settings }
          ]
        }
      ]
    }
  ];

  const filteredGroups = navigationGroups.filter((group) => group.roles.includes(user?.role));

  const isActive = (href) => {
    // Handle query string routes
    if (href.includes('?')) {
      return location.pathname + location.search === href;
    }
    return location.pathname === href;
  };

  const isGroupActive = (group) => {
    if (group.standalone && group.href) {
      return isActive(group.href);
    }
    if (group.children) {
      return group.children.some((child) => location.pathname === child.href.split('?')[0]);
    }
    // Check subgroups for settings
    if (group.subgroups) {
      return group.subgroups.some((subgroup) =>
        subgroup.children.some((child) => location.pathname === child.href.split('?')[0])
      );
    }
    return false;
  };

  const isSubgroupActive = (subgroup) => {
    return subgroup.children.some((child) => location.pathname === child.href.split('?')[0]);
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
    >
      {/* Logo */}
      <div
        className="flex items-center justify-between h-16 px-6 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Dynamic branding based on logo_type */}
          {branding.logo_type === 'none' ? (
            // Text only mode
            <span
              className={`text-white font-bold leading-tight break-words ${
                branding.sidebar_name_size === 'small'
                  ? 'text-base'
                  : branding.sidebar_name_size === 'large'
                    ? 'text-2xl'
                    : 'text-xl'
              }`}
            >
              {branding.company_name || 'Wholesale'}
            </span>
          ) : branding.logo_type === 'full' && branding.logo_url ? (
            // Full logo mode (combined logo with company name)
            <img
              src={branding.logo_url}
              alt={branding.company_name || 'Company Logo'}
              className="h-10 max-w-full object-contain"
            />
          ) : (
            // Icon + text mode (default)
            <>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{ backgroundColor: 'var(--color-accent, #D4AF37)' }}
              >
                {branding.logo_url ? (
                  <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-sm">
                    {branding.logo_icon_text || 'WS'}
                  </span>
                )}
              </div>
              <span
                className={`text-white font-bold leading-tight break-words ${
                  branding.sidebar_name_size === 'small'
                    ? 'text-base'
                    : branding.sidebar_name_size === 'large'
                      ? 'text-2xl'
                      : 'text-xl'
                }`}
              >
                {branding.company_name || 'Wholesale'}
              </span>
            </>
          )}
        </div>
        <button onClick={onClose} className="lg:hidden text-white hover:opacity-80">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 pb-24 space-y-1 overflow-y-auto overflow-x-hidden">
        {filteredGroups.map((group) => {
          const Icon = group.icon;
          const isExpanded = expandedSections.includes(group.id);
          const groupActive = isGroupActive(group);

          // Standalone item (like Dashboard)
          if (group.standalone) {
            return (
              <Link
                key={group.id}
                to={group.href}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-white/90 hover:text-white`}
                style={{
                  backgroundColor: isActive(group.href)
                    ? 'var(--wholesale-accent, #34D399)'
                    : 'transparent',
                  fontWeight: isActive(group.href) ? '600' : 'normal'
                }}
                onMouseEnter={(e) =>
                  !isActive(group.href) &&
                  (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)')
                }
                onMouseLeave={(e) =>
                  !isActive(group.href) && (e.currentTarget.style.backgroundColor = 'transparent')
                }
              >
                <Icon className="h-5 w-5" />
                <span>{group.name}</span>
              </Link>
            );
          }

          // Group with children
          return (
            <div key={group.id}>
              {/* Group Header */}
              <button
                onClick={() => toggleSection(group.id)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all text-white/90 hover:text-white"
                style={{
                  backgroundColor: groupActive ? 'rgba(255,255,255,0.1)' : 'transparent'
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = groupActive
                    ? 'rgba(255,255,255,0.1)'
                    : 'transparent')
                }
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{group.name}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {/* Group Children */}
              {isExpanded && group.children && (
                <div className="ml-4 mt-1 space-y-1">
                  {group.children
                    .filter((child) => !child.roles || child.roles.includes(user?.role))
                    .map((child) => {
                      const ChildIcon = child.icon;
                      // For exact matching, check if child has query params
                      const hasQueryParams = child.href.includes('?');
                      const childActive = hasQueryParams
                        ? isActive(child.href) // With query params, must match exactly
                        : location.pathname === child.href && !location.search; // Without query, must have no query string
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={onClose}
                          className="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-sm text-white/80 hover:text-white"
                          style={{
                            backgroundColor: childActive
                              ? 'var(--wholesale-accent, #34D399)'
                              : 'transparent',
                            fontWeight: childActive ? '600' : 'normal'
                          }}
                          onMouseEnter={(e) =>
                            !childActive &&
                            (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)')
                          }
                          onMouseLeave={(e) =>
                            !childActive &&
                            (e.currentTarget.style.backgroundColor = childActive
                              ? 'var(--wholesale-accent, #34D399)'
                              : 'transparent')
                          }
                        >
                          <ChildIcon className="h-4 w-4" />
                          <span>{child.name}</span>
                        </Link>
                      );
                    })}
                </div>
              )}

              {/* Subgroups (for Settings) */}
              {isExpanded && group.subgroups && (
                <div className="ml-4 mt-1 space-y-1">
                  {group.subgroups.map((subgroup) => {
                    const SubgroupIcon = subgroup.icon;
                    const subgroupExpanded = expandedSections.includes(subgroup.id);
                    const subgroupActive = isSubgroupActive(subgroup);

                    return (
                      <div key={subgroup.id}>
                        {/* Subgroup Header */}
                        <button
                          onClick={() => toggleSection(subgroup.id)}
                          className="w-full flex items-center justify-between px-4 py-2 rounded-lg transition-all text-sm text-white/80 hover:text-white"
                          style={{
                            backgroundColor: subgroupActive
                              ? 'rgba(255,255,255,0.1)'
                              : 'transparent'
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = subgroupActive
                              ? 'rgba(255,255,255,0.1)'
                              : 'transparent')
                          }
                        >
                          <div className="flex items-center gap-2">
                            <SubgroupIcon className="h-4 w-4" />
                            <span>{subgroup.name}</span>
                          </div>
                          {subgroupExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                        </button>

                        {/* Subgroup Children */}
                        {subgroupExpanded && (
                          <div className="ml-4 mt-1 space-y-1">
                            {subgroup.children
                              .filter((child) => !child.roles || child.roles.includes(user?.role))
                              .map((child) => {
                                const ChildIcon = child.icon;
                                const childActive = location.pathname === child.href.split('?')[0];
                                return (
                                  <Link
                                    key={child.href}
                                    to={child.href}
                                    onClick={onClose}
                                    className="flex items-center gap-3 px-4 py-2 rounded-lg transition-all text-xs text-white/80 hover:text-white"
                                    style={{
                                      backgroundColor: childActive
                                        ? 'var(--wholesale-accent, #34D399)'
                                        : 'transparent',
                                      fontWeight: childActive ? '600' : 'normal'
                                    }}
                                    onMouseEnter={(e) =>
                                      !childActive &&
                                      (e.currentTarget.style.backgroundColor =
                                        'rgba(255,255,255,0.1)')
                                    }
                                    onMouseLeave={(e) =>
                                      !childActive &&
                                      (e.currentTarget.style.backgroundColor = childActive
                                        ? 'var(--wholesale-accent, #34D399)'
                                        : 'transparent')
                                    }
                                  >
                                    <ChildIcon className="h-3 w-3" />
                                    <span>{child.name}</span>
                                  </Link>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Info */}
      <div
        className="absolute bottom-0 left-0 right-0 p-4 flex-shrink-0"
        style={{
          borderTop: '1px solid rgba(255,255,255,0.15)',
          backgroundColor: 'var(--wholesale-primary, #047857)'
        }}
      >
        <div
          className="flex items-center gap-3 px-3 py-2 rounded-lg"
          style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: 'var(--wholesale-accent, #34D399)' }}
          >
            {user?.full_name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.full_name}</p>
            <p className="text-white/70 text-xs truncate">
              {user?.role === 'super_admin'
                ? 'Super Admin'
                : user?.role === 'system_admin'
                  ? 'System Admin'
                  : user?.role === 'wholesale_admin'
                    ? 'Wholesale Admin'
                    : 'Staff'}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default WholesaleSidebar;
